import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Zap, ChevronDown, Search, Menu, X, LayoutDashboard, LogOut, Shield } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

const NAV = [
  { label: "Discover", items: [
    { to: "/discover", label: "Opportunity Feed" },
    { to: "/discover?trending=1", label: "Trending" },
    { to: "/discover?is_new=1", label: "New Opportunities" },
    { to: "/discover?sort=monetization", label: "Most Profitable" },
    { to: "/pain-points", label: "Customer Pain Points" },
  ]},
  { label: "Build", items: [
    { to: "/generate", label: "AI Idea Generator" },
    { to: "/validate", label: "Idea Validator" },
    { to: "/build-planner", label: "AI Build Planner" },
    { to: "/revenue", label: "Revenue Calculator" },
  ]},
  { label: "Intelligence", items: [
    { to: "/trends", label: "Trend Radar" },
    { to: "/competitor", label: "Competitor Radar" },
    { to: "/pain-points", label: "Pain Point Database" },
  ]},
  { label: "Resources", items: [
    { to: "/resources", label: "AI Tools & APIs" },
    { to: "/resources?category=No-Code", label: "No-Code Tools" },
    { to: "/resources?category=Automation", label: "Automation" },
  ]},
  { label: "Community", items: [
    { to: "/community", label: "Show & Tell" },
    { to: "/leaderboard", label: "Leaderboard" },
  ]},
];

export default function Navbar() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [mobile, setMobile] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/discover?search=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-white/8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16 gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-400 to-indigo-500 flex items-center justify-center">
            <Zap className="w-5 h-5 text-slate-950" fill="currentColor" />
          </div>
          <span className="font-display font-extrabold text-lg tracking-tight text-slate-50">NexxVibe</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-2">
          {NAV.map((group) => (
            <DropdownMenu key={group.label}>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 px-3 py-2 text-sm text-slate-300 hover:text-cyan-300 rounded-md transition-colors" data-testid={`nav-${group.label.toLowerCase()}`}>
                  {group.label} <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass border-white/10 text-slate-200">
                {group.items.map((it) => (
                  <DropdownMenuItem key={it.to + it.label} onClick={() => navigate(it.to)} className="cursor-pointer focus:bg-cyan-500/10 focus:text-cyan-300" data-testid={`navitem-${it.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
                    {it.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex items-center gap-2 ml-auto bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 w-56 focus-within:border-cyan-500/50 transition-colors">
          <Search className="w-4 h-4 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search opportunities…"
            className="bg-transparent text-sm text-slate-200 placeholder:text-slate-600 outline-none w-full" data-testid="global-search-input" />
        </form>

        <div className="flex items-center gap-2 ml-auto md:ml-0">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2" data-testid="user-menu-trigger">
                  <Avatar className="w-8 h-8 border border-white/10">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-cyan-500/20 text-cyan-300 text-xs">{user.name?.[0] || "U"}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="glass border-white/10 text-slate-200 w-52">
                <div className="px-2 py-2 border-b border-white/8">
                  <p className="text-sm font-medium truncate">{user.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  <span className="text-[10px] font-mono uppercase text-cyan-400">{user.plan} plan</span>
                </div>
                <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer focus:bg-cyan-500/10"><LayoutDashboard className="w-4 h-4 mr-2" />Workspace</DropdownMenuItem>
                {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-cyan-500/10"><Shield className="w-4 h-4 mr-2" />Admin</DropdownMenuItem>}
                <DropdownMenuItem onClick={logout} className="cursor-pointer focus:bg-red-500/10 text-red-300" data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" />Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" onClick={login} className="hidden sm:inline-flex text-slate-300 hover:text-cyan-300" data-testid="nav-login-btn">Log in</Button>
              <Button onClick={login} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold" data-testid="nav-cta-btn">Get Started</Button>
            </>
          )}
          <button className="lg:hidden text-slate-300" onClick={() => setMobile(!mobile)} data-testid="mobile-menu-btn">
            {mobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="lg:hidden glass border-t border-white/8 px-4 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {NAV.map((g) => (
            <div key={g.label}>
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">{g.label}</p>
              <div className="grid grid-cols-2 gap-1">
                {g.items.map((it) => (
                  <button key={it.to + it.label} onClick={() => { navigate(it.to); setMobile(false); }} className="text-left text-sm text-slate-300 py-1.5 hover:text-cyan-300">{it.label}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </header>
  );
}
